<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: DiagnosticPanel
  Reason: Input form and result rendering for the mapping-table quick test — three distinguishable target-value states, contract flags, resolution source, table size and honest iteration/timing figures.
-->

@if (initialized) {

    <div class="flex flex-column gap-4">

        <div class="flex align-items-center justify-content-between gap-2 flex-wrap">
            <span class="text-lg font-semibold">{{ getTableTitle() }}</span>
            @if (result || technicalErrorMessage) {
                <p-button icon="fa-regular fa-xmark"
                          label="Ergebnis schließen"
                          [text]="true"
                          severity="secondary"
                          (onClick)="clearResult()"></p-button>
            }
        </div>

        @if (!mappingTableId) {

            <p-message severity="info"
                       text="Wählen Sie zuerst eine Mapping-Tabelle aus, um den Schnelltest auszuführen."></p-message>

        } @else {

            <!-- Input form -->
            <div class="surface-ground border-round-lg p-3">
                <div class="grid">

                    <div class="col-12 md:col-6 mb-3">
                        <div class="flex flex-column gap-1">
                            <label class="text-sm text-gray-600" for="lgMappingSourceValue">Quellwert *</label>
                            <input id="lgMappingSourceValue"
                                   pInputText
                                   type="text"
                                   [(ngModel)]="sourceValue"
                                   maxlength="500"
                                   placeholder="Aufzulösender Wert"/>
                            @if (sourceValue && sourceValue.length > maxSourceValueLength) {
                                <small class="text-red-600">
                                    Höchstens {{ maxSourceValueLength }} Zeichen — längere Werte können
                                    keinen Eintrag treffen.
                                </small>
                            }
                        </div>
                    </div>

                    <div class="col-12 md:col-6 mb-3">
                        <div class="flex flex-column gap-1">
                            <label class="text-sm text-gray-600" for="lgMappingPartnerAlias">Partner-Alias
                                (optional)</label>
                            <input id="lgMappingPartnerAlias"
                                   pInputText
                                   type="text"
                                   [(ngModel)]="partnerAlias"
                                   placeholder="leer = keine Partner-Overrides"/>
                        </div>
                    </div>

                    <div class="col-12 md:col-6 mb-3">
                        <div class="flex flex-column gap-2">
                            <div class="flex align-items-center gap-2">
                                <p-toggleSwitch [(ngModel)]="defaultValueProvided"
                                                inputId="lgMappingDefaultProvided"></p-toggleSwitch>
                                <label class="text-sm text-gray-600" for="lgMappingDefaultProvided">
                                    Vorgabewert bei Nichttreffer übergeben
                                </label>
                            </div>
                            @if (defaultValueProvided) {
                                <input pInputText
                                       type="text"
                                       [(ngModel)]="defaultValueOnNothing"
                                       placeholder="darf auch leer bleiben"/>
                                <small class="text-gray-600">
                                    Ein leerer Text gilt als übergebener Vorgabewert. Ist der Schalter
                                    aus, wird gar kein Vorgabewert gesendet — nur dann greift ein
                                    hartes „Fehler auslösen“ der Tabelle.
                                </small>
                            }
                        </div>
                    </div>

                    <div class="col-12 md:col-6 mb-3">
                        <div class="flex flex-column gap-1">
                            <label class="text-sm text-gray-600" for="lgMappingIterations">Anzahl
                                Durchläufe</label>
                            <p-inputNumber inputId="lgMappingIterations"
                                           [(ngModel)]="iterations"
                                           [min]="minIterations"
                                           [max]="maxIterations"
                                           [showButtons]="true"
                                           [style]="{'width': '10rem'}"></p-inputNumber>
                            @if (!isIterationsValid()) {
                                <small class="text-red-600">
                                    Zulässig sind {{ minIterations }} bis {{ maxIterations }} Durchläufe.
                                </small>
                            } @else if (iterations > 1) {
                                <small class="text-gray-600">
                                    Der Lauf kann vom Server vorzeitig beendet werden, wenn das
                                    Zeitbudget erreicht ist.
                                </small>
                            }
                        </div>
                    </div>

                </div>

                <div class="flex justify-content-end">
                    <p-button label="Auflösen"
                              icon="fa-regular fa-play"
                              severity="primary"
                              [loading]="busy"
                              [disabled]="isRunDisabled()"
                              (onClick)="runTest()"></p-button>
                </div>
            </div>

            <!-- Busy -->
            @if (busy) {
                <div class="flex flex-column gap-2">
                    <p-skeleton height="3rem"></p-skeleton>
                    <p-skeleton height="1.5rem" width="60%"></p-skeleton>
                </div>
            }

            <!-- Technical failure — request never produced a result -->
            @if (technicalErrorMessage && !busy) {
                <p-message severity="error" [text]="technicalErrorMessage"></p-message>
            }

            <!-- Result -->
            @if (result && !busy) {
                <div class="surface-card border-1 surface-border border-round-lg p-3">

                    <!-- Business failure: HTTP 200 with success = false -->
                    @if (!result.success) {
                        <div class="bg-red-50 border-round-md p-3 mb-3">
                            <div class="flex align-items-center gap-2 mb-1">
                                <i class="fa-solid fa-circle-exclamation text-red-600"></i>
                                <span class="text-base font-semibold text-red-600">Auflösung fehlgeschlagen</span>
                            </div>
                            <div class="text-sm text-red-600">
                                {{ result.errorMessage || 'Es konnte kein Wert aufgelöst werden.' }}
                            </div>
                        </div>
                    }

                    <!-- Target value: three distinguishable states -->
                    <div class="mb-3">
                        <div class="text-sm text-gray-600 mb-1">Zielwert</div>
                        @if (getTargetValueState() === 'value') {
                            <div class="text-2xl font-bold">{{ result.targetValue }}</div>
                        } @else {
                            <div class="text-2xl font-bold text-gray-600">{{ getTargetValuePlaceholder() }}</div>
                            @if (getTargetValueState() === 'absent') {
                                <small class="text-gray-600">
                                    Es gibt bewusst keinen Wert — die Ausgabe „outputValue“ entfällt.
                                </small>
                            } @else {
                                <small class="text-gray-600">
                                    Treffer auf einen Eintrag, dessen Zielwert ein leerer Text ist.
                                </small>
                            }
                        }
                    </div>

                    <!-- Contract flags and resolution source -->
                    <div class="flex align-items-center gap-2 flex-wrap mb-3">
                        <p-tag [value]="result.mappingFound ? 'Treffer' : 'kein Treffer'"
                               [severity]="result.mappingFound ? 'success' : 'secondary'"
                               pTooltip="mappingFound — wahr nur bei Treffer auf einen aktiven Eintrag oder Partner-Override"
                               tooltipPosition="top"></p-tag>

                        <p-tag [value]="result.defaultValueUsed ? 'Vorgabewert genutzt' : 'Vorgabewert ungenutzt'"
                               [severity]="result.defaultValueUsed ? 'warn' : 'secondary'"
                               pTooltip="defaultValueUsed — wahr nur, wenn der Vorgabewert des Aufrufers ausgegeben wurde"
                               tooltipPosition="top"></p-tag>

                        @if (result.resolutionSource) {
                            <p-tag [value]="getResolutionSourceLabel()"
                                   [severity]="getResolutionSourceSeverity()"
                                   pTooltip="Auflösungsquelle"
                                   tooltipPosition="top"></p-tag>
                        }
                    </div>

                    <!-- Echo of what was resolved -->
                    <div class="grid mb-2">
                        <div class="col-12 md:col-6 mb-2">
                            <div class="text-sm text-gray-600">Quellwert</div>
                            <div class="text-base">{{ result.sourceValue || '—' }}</div>
                        </div>
                        <div class="col-12 md:col-6 mb-2">
                            <div class="text-sm text-gray-600">Partner-Alias</div>
                            <div class="text-base">{{ result.partnerAlias || '(keiner — Overrides übersprungen)' }}</div>
                        </div>
                        <div class="col-12 md:col-6 mb-2">
                            <div class="text-sm text-gray-600">Tabellengröße</div>
                            <div class="text-base">
                                {{ result.entryCount }} Einträge, {{ result.overrideCount }} Partner-Overrides
                            </div>
                        </div>
                        <div class="col-12 md:col-6 mb-2">
                            <div class="text-sm text-gray-600">Durchläufe</div>
                            <div class="text-base flex align-items-center gap-2 flex-wrap">
                                <span [class.text-yellow-600]="isBudgetExceeded()">{{ getIterationsText() }}</span>
                                @if (isBudgetExceeded()) {
                                    <p-tag value="vorzeitig beendet" severity="warn"></p-tag>
                                }
                            </div>
                        </div>
                    </div>

                    @if (isBudgetExceeded()) {
                        <p-message severity="warn"
                                   text="Das serverseitige Zeitbudget wurde erreicht. Alle Zeitangaben beziehen sich ausschließlich auf die tatsächlich gelaufenen Durchläufe."></p-message>
                    }

                    <p-divider></p-divider>

                    <!-- Timings -->
                    <div class="grid">
                        <div class="col-6 md:col-3 mb-2">
                            <div class="text-sm text-gray-600">Kaltwert</div>
                            <div class="text-lg font-semibold">{{ formatMicros(result.coldDurationMicros) }}</div>
                        </div>
                        @if (hasMultipleIterations()) {
                            <div class="col-6 md:col-3 mb-2">
                                <div class="text-sm text-gray-600">Warm ⌀</div>
                                <div class="text-lg font-semibold">{{ formatMicros(result.warmAverageDurationMicros) }}</div>
                            </div>
                            <div class="col-6 md:col-3 mb-2">
                                <div class="text-sm text-gray-600">Minimum</div>
                                <div class="text-lg font-semibold">{{ formatMicros(result.minDurationMicros) }}</div>
                            </div>
                            <div class="col-6 md:col-3 mb-2">
                                <div class="text-sm text-gray-600">Maximum</div>
                                <div class="text-lg font-semibold">{{ formatMicros(result.maxDurationMicros) }}</div>
                            </div>
                        }
                    </div>

                    <div class="bg-yellow-50 border-round-md p-3 mt-2">
                        <div class="flex align-items-start gap-2">
                            <i class="fa-regular fa-circle-info text-yellow-600 mt-1"></i>
                            <span class="text-sm text-yellow-600">{{ measurementNote }}</span>
                        </div>
                    </div>

                    <!-- Server warnings -->
                    @if (result.warnings?.length) {
                        <div class="flex flex-column gap-2 mt-3">
                            @for (warning of result.warnings; track warning) {
                                <div class="bg-yellow-50 border-round-md p-2 flex align-items-start gap-2">
                                    <i class="fa-solid fa-triangle-exclamation text-yellow-600 mt-1"></i>
                                    <span class="text-sm text-yellow-600">{{ warning }}</span>
                                </div>
                            }
                        </div>
                    }

                </div>
            }
        }
    </div>
}
